import GameStore from './store.js';
import { qs, qsa, delegate, toggleHidden } from './utils/dom.js';
import debounce from './utils/debounce.js';
import SearchOverlay from './components/search-overlay.js';
import { renderGames } from './components/game-grid.js';
import { trackEvent } from './utils/analytics.js';
import { toggleFavorite, isFavorite } from './utils/storage.js';

const initCardRails = () => {
  const rails = qsa('[data-card-rail]');
  if (!rails.length) return;

  rails.forEach(rail => {
    const track = rail.querySelector('[data-rail-track]') || rail.querySelector('.game-rail__inner');
    const prevWrap = rail.querySelector('[data-rail-prev]');
    const nextWrap = rail.querySelector('[data-rail-next]');
    const prevBtn = prevWrap?.querySelector('button');
    const nextBtn = nextWrap?.querySelector('button');
    if (!track || !prevBtn || !nextBtn) return;

    const getStep = () => {
      const items = Array.from(track.children).filter(node => node instanceof HTMLElement);
      if (items.length < 2) {
        return Math.max(track.clientWidth * 0.9, 240);
      }
      const first = items[0].getBoundingClientRect();
      const second = items[1].getBoundingClientRect();
      const cardSpan = second.left - first.left;
      if (cardSpan <= 0) return Math.max(track.clientWidth * 0.9, 240);
      const visible = Math.max(1, Math.floor(track.clientWidth / cardSpan));
      return cardSpan * visible;
    };

    const updateNav = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const hasOverflow = maxScroll > 4;
      prevWrap?.classList.toggle('is-hidden', !hasOverflow);
      nextWrap?.classList.toggle('is-hidden', !hasOverflow);
      if (!hasOverflow) {
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        prevWrap?.classList.add('is-disabled');
        nextWrap?.classList.add('is-disabled');
        return;
      }

      const atStart = track.scrollLeft <= 4;
      prevBtn.disabled = atStart;
      prevWrap?.classList.toggle('is-disabled', atStart);
      const atEnd = track.scrollLeft >= maxScroll - 4;
      nextBtn.disabled = atEnd;
      nextWrap?.classList.toggle('is-disabled', atEnd);
    };

    const handleNav = direction => {
      const step = getStep();
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const nextPosition = Math.min(Math.max(track.scrollLeft + direction * step, 0), maxScroll);
      track.scrollTo({ left: nextPosition, behavior: 'smooth' });
    };

    const setRailHovering = hovering => {
      rail.classList.toggle('is-hovering', hovering);
    };

    prevBtn.addEventListener('click', () => handleNav(-1));
    nextBtn.addEventListener('click', () => handleNav(1));
    rail.addEventListener('mouseenter', () => setRailHovering(true));
    rail.addEventListener('mouseleave', () => setRailHovering(false));
    track.addEventListener('pointerenter', () => setRailHovering(true));
    track.addEventListener('pointerleave', () => setRailHovering(false));
    track.addEventListener('mouseover', event => {
      const card = event.target.closest('.game-card');
      if (card && track.contains(card)) {
        setRailHovering(true);
      }
    });
    track.addEventListener('mouseout', () => {
      if (!rail.matches(':hover')) {
        setRailHovering(false);
      }
    });
    rail.addEventListener('focusin', () => setRailHovering(true));
    rail.addEventListener('focusout', event => {
      if (!rail.contains(event.relatedTarget)) {
        setRailHovering(false);
      }
    });

    const debouncedResize = debounce(updateNav, 150);
    track.addEventListener('scroll', updateNav, { passive: true });
    window.addEventListener('resize', debouncedResize);
    updateNav();
  });
};

const filterGamesByQuery = (games = [], query = '') => {
  const token = query.trim().toLowerCase();
  if (!token) return [];
  return games.filter(game => {
    const haystack = [
      game.title,
      game.provider,
      ...(game.tags || []),
      ...(game.categories || []),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(token);
  });
};

const rankSearchEntries = (entries = [], query = '') => {
  const token = query.trim().toLowerCase();
  if (!token) return [];

  const titleMatches = [];
  const tagMatches = [];
  const otherMatches = [];

  entries.forEach(entry => {
    const title = String(entry.title || '').toLowerCase();
    const tags = Array.isArray(entry.tags) ? entry.tags.map(tag => String(tag).toLowerCase()) : [];
    const provider = String(entry.provider || '').toLowerCase();
    const categories = Array.isArray(entry.categories)
      ? entry.categories.map(cat => String(cat).toLowerCase())
      : [];

    if (title.includes(token)) {
      titleMatches.push(entry);
      return;
    }
    if (tags.some(tag => tag.includes(token))) {
      tagMatches.push(entry);
      return;
    }
    if (provider.includes(token) || categories.some(cat => cat.includes(token))) {
      otherMatches.push(entry);
    }
  });

  return [...titleMatches, ...tagMatches, ...otherMatches];
};

const formatCountLabel = count => {
  if (count === 0) return 'No games found';
  if (count === 1) return '1 game found';
  return `${count} games found`;
};

const parseJson = id => {
  const el = qs(`#${id}`);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (err) {
    console.error(`Failed to parse JSON from #${id}`, err);
    return null;
  }
};

const initNavigation = () => {
  const toggle = qs('[data-nav-toggle]');
  const sidebar = qs('[data-sidebar]');
  const closeBtn = qs('[data-close-sidebar]');
  const collapseToggle = qs('[data-sidebar-toggle]');
  const shellMain = qs('[data-shell-main]');
  if (!toggle || !sidebar) return;

  const setOpen = open => {
    sidebar.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('sidebar-open', open);
    if (isMobileViewport()) {
      document.body.classList.toggle('is-sidebar-hidden', !open);
    }
  };

  const sidebarPrefKey = 'sidebar-collapsed';
  const sidebarHiddenPrefKey = 'sidebar-hidden';

  const setCollapsed = collapsed => {
    document.body.classList.toggle('is-sidebar-collapsed', collapsed);
    collapseToggle?.setAttribute('aria-expanded', String(!collapsed));
  };

  const writeCollapsedPref = value => {
    try {
      localStorage.setItem(sidebarPrefKey, String(value));
    } catch (err) {
      // Ignore storage errors (private mode, blocked, etc.).
    }
  };

  const readHiddenPref = () => {
    try {
      return localStorage.getItem(sidebarHiddenPrefKey);
    } catch (err) {
      return null;
    }
  };

  const writeHiddenPref = value => {
    try {
      localStorage.setItem(sidebarHiddenPrefKey, String(value));
    } catch (err) {
      // Ignore storage errors (private mode, blocked, etc.).
    }
  };

  const setHidden = hidden => {
    document.body.classList.toggle('is-sidebar-hidden', hidden);
    collapseToggle?.setAttribute('aria-expanded', String(!hidden));
  };

  const isMobileViewport = () => window.matchMedia('(max-width: 1024px)').matches;

  const handleSidebarToggle = () => {
    if (isMobileViewport()) {
      const isOpen = sidebar.classList.contains('is-open');
      setOpen(!isOpen);
      return;
    }
    const isHidden = document.body.classList.contains('is-sidebar-hidden');
    setHidden(!isHidden);
    writeHiddenPref(!isHidden);
  };

  if (collapseToggle && collapseToggle === toggle) {
    toggle.addEventListener('click', handleSidebarToggle);
  } else {
    toggle.addEventListener('click', () => {
      const isOpen = sidebar.classList.contains('is-open');
      setOpen(!isOpen);
    });

    collapseToggle?.addEventListener('click', handleSidebarToggle);
  }

  const applySidebarMode = () => {
    if (isMobileViewport()) {
      setOpen(false);
      setCollapsed(false);
      return;
    }

    // Desktop sidebar is icon-only by default.
    setCollapsed(true);
    writeCollapsedPref(true);

    const storedHiddenPref = readHiddenPref();
    if (storedHiddenPref !== null) {
      setHidden(storedHiddenPref === 'true');
    } else {
      setHidden(false);
    }
  };

  applySidebarMode();
  window.addEventListener('resize', debounce(applySidebarMode, 120));

  closeBtn?.addEventListener('click', () => setOpen(false));

  shellMain?.addEventListener('click', event => {
    if (!sidebar.classList.contains('is-open')) return;
    if (toggle.contains(event.target)) return;
    if (!sidebar.contains(event.target)) {
      setOpen(false);
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && sidebar.classList.contains('is-open')) {
      setOpen(false);
    }
  });
};

const updateActiveChip = (chips, value) => {
  chips.forEach(chip => {
    const isActive = chip.dataset.filterValue === value;
    chip.classList.toggle('is-active', isActive);
    chip.setAttribute('aria-checked', String(isActive));
  });
};

const updateUrl = (state, options = {}) => {
  const { skipCategory = false } = options;
  const url = new URL(window.location.href);
  if (skipCategory) {
    url.searchParams.delete('category');
  } else if (state.category && state.category !== 'all') {
    url.searchParams.set('category', state.category);
  } else {
    url.searchParams.delete('category');
  }

  if (state.search) {
    url.searchParams.set('q', state.search);
  } else {
    url.searchParams.delete('q');
  }

  if (state.sort && state.sort !== 'latest') {
    url.searchParams.set('sort', state.sort);
  } else {
    url.searchParams.delete('sort');
  }

  if (state.page > 1) {
    url.searchParams.set('page', String(state.page));
  } else {
    url.searchParams.delete('page');
  }

  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', next);
};

const initListingPage = games => {
  const grid = qs('[data-game-grid]');
  if (!grid) return;

  const filterGroup = qs('[data-filter-categories]');
  const sortSelect = qs('[data-sort-select]');
  const inlineSearch = qs('[data-inline-search]');
  const loadMoreBtn = qs('[data-load-more]');
  const emptyState = qs('[data-empty-state]');
  const resetBtn = qs('[data-reset-filters]');
  const categorySection = qs('[data-initial-category]');
  const loadedProgress = qs('[data-loaded-progress]');

  const perPage = 24;
  const params = new URLSearchParams(window.location.search);
  const categoryParam = params.get('category');
  const sortParam = params.get('sort');
  const searchParam = params.get('q');
  const pageParam = Number.parseInt(params.get('page'), 10);
  const initialCategory = categorySection
    ? categorySection.dataset.initialCategory
    : categoryParam || 'all';
  const isCategoryPage = Boolean(categorySection);
  const totalFromMarkup = Number.parseInt(categorySection?.dataset.totalGames || '', 10);
  const totalAvailable = Number.isNaN(totalFromMarkup)
    ? games.length
    : Math.max(totalFromMarkup, games.length);
  const listingMetaPath = categorySection?.dataset.listingMeta || '';
  const listingBasePath = listingMetaPath.replace(/\/meta\.json$/, '');
  const isPagedListingEnabled = Boolean(listingMetaPath && listingBasePath);
  const loadedPages = new Set(isPagedListingEnabled ? [1] : []);
  const remotePagePromises = new Map();
  let listingMeta = null;
  let listingMetaPromise = null;

  const store = new GameStore(games, {
    category: initialCategory,
    perPage,
    sort: sortParam || 'latest',
    search: searchParam || '',
    page: Number.isNaN(pageParam) ? 1 : Math.max(pageParam, 1),
  });

  const chips = qsa('[data-filter-value]', filterGroup || document);
  updateActiveChip(chips, initialCategory || 'all');

  const getTotalPages = () => {
    if (listingMeta?.totalPages) return listingMeta.totalPages;
    return Math.max(1, Math.ceil(totalAvailable / perPage));
  };

  const hasRemotePagesRemaining = () =>
    isPagedListingEnabled && loadedPages.size < getTotalPages();

  const canUseRemotePagination = state =>
    isPagedListingEnabled
    && state.category === initialCategory
    && !state.search
    && state.sort === 'latest'
    && totalAvailable > 0;

  const resolvePagePath = page => {
    if (listingMeta?.pagePathTemplate) {
      return listingMeta.pagePathTemplate.replace('{page}', String(page));
    }
    return `${listingBasePath}/page-${page}.json`;
  };

  const ensureListingMeta = async () => {
    if (!isPagedListingEnabled) return null;
    if (listingMeta) return listingMeta;
    if (!listingMetaPromise) {
      listingMetaPromise = (async () => {
        const res = await fetch(listingMetaPath);
        if (!res.ok) throw new Error(`Failed to load listing meta: ${res.status}`);
        const payload = await res.json();
        if (!payload || typeof payload !== 'object') {
          throw new Error('Invalid listing meta payload');
        }
        listingMeta = payload;
        return listingMeta;
      })()
        .catch(err => {
          console.error('Failed to load listing meta', err);
          return null;
        })
        .finally(() => {
          listingMetaPromise = null;
        });
    }
    return listingMetaPromise;
  };

  const appendRemotePage = async page => {
    if (!isPagedListingEnabled) return true;
    if (loadedPages.has(page)) return true;
    if (remotePagePromises.has(page)) return remotePagePromises.get(page);

    const request = (async () => {
      const res = await fetch(resolvePagePath(page));
      if (!res.ok) throw new Error(`Failed to load listing page ${page}: ${res.status}`);
      const chunk = await res.json();
      if (!Array.isArray(chunk)) throw new Error(`Listing page ${page} is not an array`);

      const existing = store.games;
      const knownSlugs = new Set(existing.map(game => game.slug));
      const merged = [...existing];
      chunk.forEach(game => {
        if (!knownSlugs.has(game.slug)) {
          knownSlugs.add(game.slug);
          merged.push(game);
        }
      });
      loadedPages.add(page);
      store.replaceGames(merged);
      return true;
    })()
      .catch(err => {
        console.error('Failed to append listing page', err);
        return false;
      })
      .finally(() => {
        remotePagePromises.delete(page);
      });

    remotePagePromises.set(page, request);
    return request;
  };

  const ensurePagesThrough = async targetPage => {
    if (!isPagedListingEnabled) return true;
    await ensureListingMeta();
    const safeTarget = Math.max(1, Math.min(targetPage, getTotalPages()));
    const pagesToLoad = Array.from({ length: safeTarget - 1 }, (_, index) => index + 2);
    const results = await pagesToLoad.reduce(
      (chain, page) =>
        chain.then(async allOk => {
          if (!allOk) return false;
          return appendRemotePage(page);
        }),
      Promise.resolve(true)
    );
    return results;
  };

  const ensureAllPagesLoaded = async () => {
    if (!isPagedListingEnabled) return true;
    await ensureListingMeta();
    return ensurePagesThrough(getTotalPages());
  };

  const updateLoadedProgress = state => {
    if (!loadedProgress || !isCategoryPage) return;
    const loadedCount = Math.min(store.games.length, totalAvailable);
    const totalLabel = new Intl.NumberFormat('en-US').format(totalAvailable);
    const loadedLabel = new Intl.NumberFormat('en-US').format(loadedCount);
    if (state.search) loadedProgress.dataset.searching = 'true';
    else loadedProgress.dataset.searching = 'false';
    loadedProgress.textContent = `Loaded ${loadedLabel} / ${totalLabel}`;
  };

  const overlay = new SearchOverlay(document, {
    onSearch: query => {
      if (inlineSearch) inlineSearch.value = query;
      const run = async () => {
        if (query && isPagedListingEnabled) {
          await ensureAllPagesLoaded();
        }
        store.setSearch(query);
      };
      run();
    },
  });

  store.subscribe(snapshot => {
    const { state, visible, total, hasMore } = snapshot;
    renderGames(grid, visible);
    updateLoadedProgress(state);

    toggleHidden(emptyState, total !== 0);
    if (loadMoreBtn) {
      const hasRemoteMore = canUseRemotePagination(state) && hasRemotePagesRemaining();
      toggleHidden(loadMoreBtn, !(hasMore || hasRemoteMore));
    }

    if (inlineSearch && inlineSearch.value !== state.search) {
      inlineSearch.value = state.search;
    }
    overlay?.setValue?.(state.search);
    if (sortSelect && sortSelect.value !== state.sort) {
      sortSelect.value = state.sort;
    }
    updateActiveChip(chips, state.category);
    updateUrl(state, { skipCategory: isCategoryPage });
  });

  if (filterGroup) {
    delegate(filterGroup, '[data-filter-value]', 'click', async (event, target) => {
      event.preventDefault();
      const value = target.dataset.filterValue;
      if (value !== initialCategory && isPagedListingEnabled) {
        await ensureAllPagesLoaded();
      }
      store.setCategory(value);
      trackEvent('filter_category', { category: value });
    });
  }

  if (sortSelect) {
    sortSelect.addEventListener('change', async event => {
      const nextSort = event.target.value;
      if (nextSort !== 'latest' && isPagedListingEnabled) {
        await ensureAllPagesLoaded();
      }
      store.setSort(nextSort);
      trackEvent('sort_change', { sort: nextSort });
    });
  }

  if (inlineSearch) {
    inlineSearch.addEventListener(
      'input',
      debounce(async event => {
        const nextSearch = event.target.value.trim();
        if (nextSearch && isPagedListingEnabled) {
          await ensureAllPagesLoaded();
        }
        store.setSearch(nextSearch);
      }, 250)
    );
  }

  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      store.resetFilters();
      trackEvent('filters_reset');
    });
  }

  if (loadMoreBtn) {
    loadMoreBtn.addEventListener('click', async () => {
      const { state } = store.getSnapshot();
      if (!canUseRemotePagination(state)) {
        store.loadMore();
        trackEvent('load_more');
        return;
      }

      if (!store.hasMore() && hasRemotePagesRemaining()) {
        const nextPage = loadedPages.size + 1;
        await appendRemotePage(nextPage);
      }
      store.loadMore();
      trackEvent('load_more');
    });
  }

  const bootstrapRemoteListing = async () => {
    if (!isPagedListingEnabled) return;
    await ensureListingMeta();
    if (!Number.isNaN(pageParam) && pageParam > 1) {
      await ensurePagesThrough(pageParam);
    }

    const needsFullDatasetOnInit = Boolean(
      (searchParam && searchParam.trim()) || (sortParam && sortParam !== 'latest')
    );
    if (needsFullDatasetOnInit) {
      await ensureAllPagesLoaded();
    }
  };

  if (isPagedListingEnabled) {
    bootstrapRemoteListing();
  }
};

const initSearchPage = () => {
  const pageRoot = qs('[data-search-page]');
  if (!pageRoot) return;

  const grid = qs('[data-search-grid]', pageRoot);
  if (!grid) return;

  const summary = qs('[data-search-summary]');
  const title = qs('[data-search-title]');
  const countEl = qs('[data-search-count]');
  const emptyState = qs('[data-search-empty]', pageRoot);
  const emptyDefault = qs('[data-search-empty-default]', pageRoot);
  const emptyNoResults = qs('[data-search-empty-noresults]', pageRoot);
  const termEl = qs('[data-search-term]', pageRoot);
  const searchIndexPath = pageRoot.dataset.searchIndex || '/data/search.json';
  const searchCardsPath = pageRoot.dataset.searchCards || '/data/search-cards.json';

  let searchIndex = null;
  let searchCards = null;
  let searchDataPromise = null;

  const ensureSearchData = async () => {
    if (Array.isArray(searchIndex) && searchCards && typeof searchCards === 'object') {
      return true;
    }
    if (!searchDataPromise) {
      searchDataPromise = Promise.all([fetch(searchIndexPath), fetch(searchCardsPath)])
        .then(async ([indexRes, cardsRes]) => {
          if (!indexRes.ok) throw new Error(`Failed to load search index: ${indexRes.status}`);
          if (!cardsRes.ok) throw new Error(`Failed to load search cards: ${cardsRes.status}`);
          const [indexPayload, cardsPayload] = await Promise.all([
            indexRes.json(),
            cardsRes.json(),
          ]);
          if (!Array.isArray(indexPayload)) throw new Error('Search index payload is not an array');
          if (!cardsPayload || typeof cardsPayload !== 'object') {
            throw new Error('Search cards payload is invalid');
          }
          searchIndex = indexPayload;
          searchCards = cardsPayload;
          return true;
        })
        .catch(err => {
          console.error('Failed to load search data', err);
          return false;
        })
        .finally(() => {
          searchDataPromise = null;
        });
    }
    return searchDataPromise;
  };

  const applyQuery = async () => {
    const params = new URLSearchParams(window.location.search);
    const query = params.get('q')?.trim() || '';

    if (termEl) termEl.textContent = query;

    if (title) {
      title.textContent = query ? `Results for "${query}"` : 'Search the catalog';
    }

    if (summary) {
      summary.textContent = query
        ? `Search results for "${query}".`
        : 'Enter a term to explore games.';
    }

    if (!query) {
      if (countEl) countEl.textContent = '';
      renderGames(grid, []);
      toggleHidden(emptyState, false);
      toggleHidden(emptyDefault, false);
      toggleHidden(emptyNoResults, true);
      return;
    }

    const dataReady = await ensureSearchData();
    if (!dataReady || !Array.isArray(searchIndex)) {
      if (countEl) countEl.textContent = '';
      renderGames(grid, []);
      toggleHidden(emptyState, false);
      toggleHidden(emptyDefault, true);
      toggleHidden(emptyNoResults, false);
      return;
    }

    const matches = rankSearchEntries(searchIndex, query)
      .map(entry => searchCards?.[entry.slug])
      .filter(Boolean);

    if (countEl) {
      countEl.textContent = formatCountLabel(matches.length);
    }

    if (matches.length === 0) {
      renderGames(grid, []);
      toggleHidden(emptyState, false);
      toggleHidden(emptyDefault, true);
      toggleHidden(emptyNoResults, false);
      return;
    }

    renderGames(grid, matches);
    toggleHidden(emptyState, true);
  };

  applyQuery();
  window.addEventListener('popstate', () => {
    applyQuery();
  });
};

const initGameDetail = game => {
  const favoritesBtn = qs('[data-favorite-toggle]');
  const label = qs('[data-favorite-label]');
  const shareBtn = qs('[data-share-game]');

  if (favoritesBtn && label) {
    const updateState = () => {
      const active = isFavorite(game.slug);
      favoritesBtn.classList.toggle('is-active', active);
      label.textContent = active ? 'Saved' : 'Save Game';
    };

    favoritesBtn.addEventListener('click', () => {
      toggleFavorite(game.slug);
      updateState();
      trackEvent('favorite_toggle', { slug: game.slug });
    });

    updateState();
  }

  if (shareBtn) {
    shareBtn.addEventListener('click', async () => {
      const shareData = {
        title: game.title,
        text: game.description,
        url: window.location.href,
      };
      if (navigator.share) {
        try {
          await navigator.share(shareData);
          trackEvent('share_native', { slug: game.slug });
        } catch (err) {
          console.warn('Share cancelled', err);
        }
      } else if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(shareData.url);
          shareBtn.textContent = 'Link copied';
          setTimeout(() => {
            shareBtn.textContent = 'Share with Friends';
          }, 2000);
          trackEvent('share_clipboard', { slug: game.slug });
        } catch (err) {
          console.error('Clipboard failed', err);
        }
      }
    });
  }
};

const bootstrap = () => {
  initNavigation();
  initCardRails();

  const gamesData = parseJson('all-games');
  if (Array.isArray(gamesData) && gamesData.length) {
    initListingPage(gamesData);
  }
  initSearchPage();

  const gameDetail = parseJson('game-detail');
  if (gameDetail) {
    initGameDetail(gameDetail);
  }
};

document.addEventListener('DOMContentLoaded', bootstrap);
